import { useEffect, useMemo, useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import {
  platformApi,
  type BusinessType,
  type PlatformBusinessType,
  type PlatformPlan,
  type PlatformTenant,
} from "@/core/api/platformApi";
import { ApiError, getErrorMessage } from "@/core/utils/errors";
import { AppIcon } from "@/shared/components/AppIcon";
import { JalaliDatePicker } from "@/shared/components/JalaliDateTimePicker";
import { isValidJalaaliDate, toGregorian, toJalaali } from "jalaali-js";

interface TenantForm {
  name: string;
  code: string;
  businessType: BusinessType;
  planName: string;
  startsAt: string;
  endsAt: string;
  maxCustomerAccounts: string;
  maxStaff: string;
  maxLeads: string;
  maxCatalogItems: string;
  ownerDisplayName: string;
  ownerUsername: string;
  ownerEmail: string;
  ownerPassword: string;
  ownerMobile: string;
  planId: string;
  smsEnabled: boolean;
  smsCreditEnabled: boolean;
  smsCreditLimit: string;
  emailEnabled: boolean;
  socialEnabled: boolean;
}

interface CompanyEditor {
  tenantId: string;
  name: string;
  code: string;
  businessType: BusinessType;
  displayName: string;
  username: string;
  email: string;
  mobile: string;
  newPassword: string;
}

interface SmsCreditEditor {
  tenantId: string;
  tenantName: string;
  enabled: boolean;
  limit: string;
  used: number;
  topUp: string;
}

interface CredentialResendEditor {
  tenantId: string;
  tenantName: string;
  managerName: string;
  username: string;
  mobile: string;
  newPassword: string;
}

const pad = (value: number): string => String(value).padStart(2, "0");
const toJalaliInput = (value: Date | string): string => {
  const source = typeof value === "string" ? new Date(value) : value;
  const { jy, jm, jd } = toJalaali(source);
  return `${jy}/${pad(jm)}/${pad(jd)}`;
};
const today = (): string => toJalaliInput(new Date());
const nextYear = (): string => {
  const { jy, jm, jd } = toJalaali(new Date());
  return `${jy + 1}/${pad(jm)}/${pad(jd)}`;
};
const initialForm = (businessType = "Other"): TenantForm => ({
  name: "",
  code: "",
  businessType,
  planName: "حرفه‌ای",
  startsAt: today(),
  endsAt: nextYear(),
  maxCustomerAccounts: "500",
  maxStaff: "10",
  maxLeads: "500",
  maxCatalogItems: "0",
  ownerDisplayName: "",
  ownerUsername: "",
  ownerEmail: "",
  ownerPassword: "",
  ownerMobile: "",
  planId: "",
  smsEnabled: false,
  smsCreditEnabled: false,
  smsCreditLimit: "0",
  emailEnabled: false,
  socialEnabled: false,
});

const businessTypeInfo = (value: BusinessType | null | undefined, types: PlatformBusinessType[]) => {
  const item = types.find((type) => type.code === value);
  return item
    ? { label: item.name, template: `قالب ${item.name}`, description: item.description || `قالب قابل شخصی‌سازی برای ${item.entityName}.`, featureCodes: item.templateFeatureCodes ?? [] }
    : { label: value || 'سایر', template: 'قالب عمومی', description: 'شروع خالی و قابل شخصی‌سازی برای هر مدل کسب‌وکار.', featureCodes: [] as string[] };
};
const asIso = (value: string): string => {
  const normalized = value
    .trim()
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)));
  const match = normalized.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (!match) throw new Error("تاریخ را با الگوی ۱۴۰۵/۰۱/۰۱ وارد کنید.");
  const [, year, month, day] = match;
  const jy = Number(year);
  const jm = Number(month);
  const jd = Number(day);
  if (!isValidJalaaliDate(jy, jm, jd))
    throw new Error("تاریخ شمسی معتبر نیست.");
  const gregorian = toGregorian(jy, jm, jd);
  return new Date(
    Date.UTC(gregorian.gy, gregorian.gm - 1, gregorian.gd),
  ).toISOString();
};
const toPickerValue = (value: string): Dayjs | undefined => {
  const normalized = value
    .trim()
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)));
  const match = normalized.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (!match) return undefined;
  const [, year, month, day] = match;
  const jy = Number(year);
  const jm = Number(month);
  const jd = Number(day);
  if (!isValidJalaaliDate(jy, jm, jd)) return undefined;
  const gregorian = toGregorian(jy, jm, jd);
  return dayjs(new Date(gregorian.gy, gregorian.gm - 1, gregorian.gd));
};
const date = (value: string): string => toJalaliInput(value);
const number = (value: number): string =>
  new Intl.NumberFormat("fa-IR").format(value);
const limit = (value: number): string =>
  value === 0 ? "نامحدود" : number(value);
const usagePercent = (used: number, maximum: number): number =>
  maximum === 0 ? 0 : Math.min(100, Math.round((used / maximum) * 100));

const tenantCodePattern = /^[A-Za-z0-9-]+$/;
const ownerUsernamePattern = /^[A-Za-z0-9._@-]{3,80}$/;
const validateCreateForm = (
  form: TenantForm,
  startsAtUtc: string,
  endsAtUtc: string,
): string | null => {
  if (!form.name.trim() || form.name.trim().length > 250)
    return "نام شرکت الزامی است و نباید بیشتر از ۲۵۰ کاراکتر باشد.";
  if (!tenantCodePattern.test(form.code.trim()) || form.code.trim().length > 80)
    return "کد سازمانی فقط می‌تواند شامل حروف انگلیسی، عدد و خط تیره باشد.";
  if (!form.planId && !form.planName.trim()) return "نام پلن الزامی است.";
  if (Date.parse(endsAtUtc) <= Date.parse(startsAtUtc))
    return "تاریخ پایان لایسنس باید بعد از تاریخ شروع باشد.";
  const limits = [form.maxCustomerAccounts, form.maxStaff, form.maxLeads, form.maxCatalogItems, form.smsCreditLimit];
  if (limits.some((value) => !Number.isFinite(Number(value)) || Number(value) < 0))
    return "ظرفیت‌های لایسنس باید عدد صفر یا یک عدد مثبت باشند.";
  if (!form.ownerDisplayName.trim()) return "نام مدیر شرکت الزامی است.";
  if (!ownerUsernamePattern.test(form.ownerUsername.trim()))
    return "نام کاربری باید بین ۳ تا ۸۰ کاراکتر و فقط شامل حروف انگلیسی، عدد، نقطه، زیرخط، @ یا خط تیره باشد.";
  if (!/^\S+@\S+\.\S+$/.test(form.ownerEmail.trim())) return "ایمیل مدیر شرکت معتبر نیست.";
  if (form.ownerPassword.length < 8) return "رمز عبور مدیر شرکت باید حداقل ۸ کاراکتر باشد.";
  const mobileDigitCount = form.ownerMobile.match(/[0-9۰-۹٠-٩]/g)?.length ?? 0;
  if (mobileDigitCount < 7 || mobileDigitCount > 15) return "شماره موبایل مدیر شرکت معتبر نیست.";
  return null;
};

const generateTemporaryPassword = (): string => {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#_-";
  const values = new Uint32Array(12);
  window.crypto.getRandomValues(values);
  return Array.from(values, (value) => alphabet[value % alphabet.length]).join("");
};

const UsageMeter = ({
  label,
  used,
  maximum,
}: {
  label: string;
  used: number;
  maximum: number;
}): React.ReactElement => {
  const percent = usagePercent(used, maximum);
  return (
    <div className="w-full min-w-0 xl:min-w-36">
      <div className="flex items-center justify-between gap-3 text-[11px] font-bold">
        <span className="text-slate-500">{label}</span>
        <span className="text-slate-700">
          {number(used)} از {limit(maximum)}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full ${percent >= 90 ? "bg-rose-500" : percent >= 70 ? "bg-amber-500" : "bg-brand-500"}`}
          style={{ width: maximum === 0 ? "100%" : `${percent}%`, opacity: maximum === 0 ? 0.25 : 1 }}
        />
      </div>
    </div>
  );
};

interface TenantCardProps {
  tenant: PlatformTenant;
  businessTypes: PlatformBusinessType[];
  onEditCompany: (tenant: PlatformTenant) => void;
  onEditLicense: (tenant: PlatformTenant) => void;
  onEditCredit: (tenant: PlatformTenant) => void;
  onResendLogin: (tenant: PlatformTenant) => void;
  onToggleTenant: (tenant: PlatformTenant) => void;
  onDeleteTenant: (tenant: PlatformTenant) => void;
}

const TenantCard = ({
  tenant,
  businessTypes,
  onEditCompany,
  onEditLicense,
  onEditCredit,
  onResendLogin,
  onToggleTenant,
  onDeleteTenant,
}: TenantCardProps): React.ReactElement => (
  <article className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/50 p-4 sm:p-5">
    <div className="flex min-w-0 items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="truncate text-sm font-black text-slate-800">
          {tenant.name}
        </h3>
        <p
          className="mt-1 truncate font-sans text-xs text-slate-400"
          dir="ltr"
        >
          {tenant.code}
        </p>
        <p className="mt-2 text-[10px] font-bold text-brand-600">
          {businessTypeInfo(tenant.businessType, businessTypes).label} · {tenant.suggestedTemplate?.name ?? businessTypeInfo(tenant.businessType, businessTypes).template}
        </p>
      </div>
      <span
        className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${tenant.status === "Active" && tenant.subscription.isUsable ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}
      >
        {tenant.status !== "Active"
          ? "شرکت غیرفعال"
          : tenant.subscription.isUsable
            ? "فعال"
            : "لایسنس غیرفعال / منقضی"}
      </span>
    </div>

    <dl className="mt-4 grid grid-cols-2 gap-3 text-xs">
      <div className="min-w-0 rounded-xl bg-white p-3">
        <dt className="text-slate-400">پلن</dt>
        <dd className="mt-1 truncate font-black text-slate-700">
          {tenant.subscription.planName}
        </dd>
      </div>
      <div className="min-w-0 rounded-xl bg-white p-3">
        <dt className="text-slate-400">پایان اعتبار</dt>
        <dd className="mt-1 font-black text-slate-700">
          {date(tenant.subscription.endsAtUtc)}
        </dd>
      </div>
    </dl>

    <div className="mt-4 space-y-4 rounded-xl bg-white p-3.5">
      <UsageMeter
        label="مشتری"
        maximum={tenant.subscription.maxCustomerAccounts}
        used={tenant.usage.customerAccounts}
      />
      <UsageMeter
        label="پرسنل فعال"
        maximum={tenant.subscription.maxStaff}
        used={tenant.usage.activeStaff}
      />
      <p className="border-t border-slate-100 pt-3 text-[11px] font-bold text-slate-500">
        سقف ثبت سرنخ: <span className="text-slate-700">{limit(tenant.subscription.maxLeads)}</span>
      </p>
    </div>

    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3">
        <p className="text-xs font-black text-slate-700">کانال‌های لایسنس</p>
        <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] font-black">
          <span className={`rounded-full px-2 py-1 ${tenant.channels.emailAllowed ? "bg-white text-emerald-700" : "bg-white/60 text-slate-400"}`}>ایمیل</span>
          <span className={`rounded-full px-2 py-1 ${tenant.channels.smsAllowed ? "bg-white text-emerald-700" : "bg-white/60 text-slate-400"}`}>پیامک</span>
          <span className={`rounded-full px-2 py-1 ${tenant.channels.socialAllowed ? "bg-white text-emerald-700" : "bg-white/60 text-slate-400"}`}>شبکه اجتماعی</span>
        </div>
        <p className="mt-2 text-[10px] font-bold text-slate-400">تغییر از «ویرایش لایسنس» انجام می‌شود.</p>
      </div>
      <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-3">
        <p className="text-xs font-black text-slate-700">شارژ پیامک</p>
        <p
          className={`mt-1 text-[11px] font-black ${tenant.subscription.smsCreditEnabled ? "text-violet-700" : "text-slate-400"}`}
        >
          {!tenant.subscription.smsCreditEnabled
            ? "غیرفعال"
            : tenant.subscription.smsCreditUnlimited
              ? "نامحدود"
              : `${number(tenant.subscription.smsCreditRemaining ?? 0)} باقی‌مانده`}
        </p>
        {tenant.subscription.smsCreditEnabled &&
        !tenant.subscription.smsCreditUnlimited ? (
          <p className="mt-1 text-[10px] text-slate-400">
            {number(tenant.subscription.smsCreditUsed)} مصرف از {number(tenant.subscription.smsCreditLimit)}
          </p>
        ) : null}
      </div>
    </div>

    <div className="mt-4 grid gap-2 sm:grid-cols-2">
      <button
        className="rounded-xl bg-white px-3 py-2.5 text-xs font-bold text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-100"
        onClick={() => onEditCompany(tenant)}
        type="button"
      >
        ویرایش شرکت
      </button>
      <button
        className="rounded-xl bg-brand-50 px-3 py-2.5 text-xs font-bold text-brand-700 hover:bg-brand-100"
        onClick={() => onEditLicense(tenant)}
        type="button"
      >
        ویرایش لایسنس
      </button>
      <button
        className="rounded-xl bg-violet-50 px-3 py-2.5 text-xs font-bold text-violet-700 hover:bg-violet-100"
        onClick={() => onEditCredit(tenant)}
        type="button"
      >
        مدیریت شارژ
      </button>
      <button
        className="rounded-xl bg-emerald-50 px-3 py-2.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100"
        onClick={() => onResendLogin(tenant)}
        type="button"
      >
        ارسال مجدد اطلاعات ورود
      </button>
      <button
        className={`rounded-xl px-3 py-2.5 text-xs font-bold ${tenant.status === "Active" ? "bg-amber-50 text-amber-700 hover:bg-amber-100" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
        onClick={() => onToggleTenant(tenant)}
        type="button"
      >
        {tenant.status === "Active" ? "غیرفعال کردن شرکت" : "فعال کردن شرکت"}
      </button>
      <button
        className="rounded-xl bg-rose-50 px-3 py-2.5 text-xs font-bold text-rose-700 hover:bg-rose-100"
        onClick={() => onDeleteTenant(tenant)}
        type="button"
      >
        حذف منطقی شرکت
      </button>
    </div>
  </article>
);

export const PlatformTenantsPage = (): React.ReactElement => {
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [plans, setPlans] = useState<PlatformPlan[]>([]);
  const [businessTypes, setBusinessTypes] = useState<PlatformBusinessType[]>([]);
  const [form, setForm] = useState<TenantForm>(initialForm);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [createdLogin, setCreatedLogin] = useState<{
    code: string;
    username: string;
    reusedExistingUser: boolean;
    smsSent: boolean | null;
    smsError: string | null;
  } | null>(null);
  const [editing, setEditing] = useState<PlatformTenant | null>(null);
  const [editingCompany, setEditingCompany] = useState<CompanyEditor | null>(
    null,
  );
  const [creditEditor, setCreditEditor] = useState<SmsCreditEditor | null>(null);
  const [credentialEditor, setCredentialEditor] = useState<CredentialResendEditor | null>(null);
  const [filter, setFilter] = useState("");

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const [tenantItems, planItems, businessTypeItems] = await Promise.all([
        platformApi.list(),
        platformApi.listPlans(),
        platformApi.listBusinessTypes(),
      ]);
      setTenants(tenantItems);
      setPlans(planItems);
      setBusinessTypes(businessTypeItems);
    } catch (loadError) {
      if (getErrorMessage(loadError)) setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timeoutId = window.setTimeout(() => setNotice(null), 9000);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  const visibleTenants = useMemo(
    () =>
      tenants.filter((tenant) =>
        `${tenant.name} ${tenant.code}`
          .toLocaleLowerCase()
          .includes(filter.trim().toLocaleLowerCase()),
      ),
    [filter, tenants],
  );
  const activeBusinessTypes = useMemo(() => businessTypes.filter((item) => item.isActive), [businessTypes]);
  const availablePlans = useMemo(
    () => plans.filter((plan) => !(plan.businessTypeCodes?.length) || plan.businessTypeCodes.includes(form.businessType)),
    [form.businessType, plans],
  );
  const selectedBusinessType = businessTypeInfo(form.businessType, businessTypes);
  const update = (field: keyof TenantForm, value: string): void =>
    setForm((current) => ({ ...current, [field]: value }));
  const focusPlatformSection = (section: "company" | "tenants"): void => {
    setIsCreateFormOpen(section === "company");
    const sectionId = section === "company" ? "platform-company-create" : "platform-tenants";
    window.requestAnimationFrame(() =>
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };
  const createTenant = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setCreatedLogin(null);
    setSubmitting(true);
    try {
      const startsAtUtc = asIso(form.startsAt);
      const endsAtUtc = asIso(form.endsAt);
      const validationError = validateCreateForm(form, startsAtUtc, endsAtUtc);
      if (validationError) throw new Error(validationError);
      const normalizedCode = form.code.trim().toLowerCase();
      if (tenants.some((tenant) => tenant.code.toLowerCase() === normalizedCode))
        throw new Error("این کد سازمانی قبلاً ثبت شده است. لطفاً کد دیگری وارد کنید.");
      const payload = {
        name: form.name.trim(),
        code: normalizedCode,
        businessType: form.businessType,
        planName: form.planName.trim(),
        startsAtUtc,
        endsAtUtc,
        maxCustomerAccounts: Number(form.maxCustomerAccounts),
        maxStaff: Number(form.maxStaff),
        maxLeads: Number(form.maxLeads),
        maxCatalogItems: Number(form.maxCatalogItems),
        ownerDisplayName: form.ownerDisplayName.trim(),
        ownerUsername: form.ownerUsername.trim(),
        ownerEmail: form.ownerEmail.trim(),
        ownerPassword: form.ownerPassword,
        ownerMobile: form.ownerMobile.trim(),
        planId: form.planId || null,
        smsEnabled: form.smsEnabled,
        smsCreditEnabled: form.smsCreditEnabled,
        smsCreditLimit: Number(form.smsCreditLimit),
        emailEnabled: form.emailEnabled,
        socialEnabled: form.socialEnabled,
        confirmExistingOwner: false,
      };
      let created: PlatformTenant;
      try {
        created = await platformApi.create(payload);
      } catch (initialCreateError) {
        const reuseWarning = initialCreateError instanceof ApiError
          ? initialCreateError.problem?.errors?.ownerReuseConfirmation?.[0]
          : undefined;
        if (!reuseWarning) throw initialCreateError;
        if (!window.confirm(reuseWarning)) {
          setNotice("ثبت شرکت لغو شد و هیچ اطلاعاتی ذخیره نشد.");
          return;
        }
        created = await platformApi.create({ ...payload, confirmExistingOwner: true });
      }
      setTenants((current) =>
        [...current, created].sort((a, b) =>
          a.name.localeCompare(b.name, "fa"),
        ),
      );
      setForm(initialForm(activeBusinessTypes[0]?.code ?? "Other"));
      setIsCreateFormOpen(false);
      setNotice(`فضای CRM «${created.name}» و مدیر اولیه آن ایجاد شد.`);
      setCreatedLogin({
        code: created.code,
        username: created.administratorUsername ?? form.ownerUsername,
        reusedExistingUser: created.initialOwner?.reusedExistingUser ?? false,
        smsSent: created.onboardingSmsSent ?? null,
        smsError: created.onboardingSmsError ?? null,
      });
    } catch (createError) {
      setError(getErrorMessage(createError));
    } finally {
      setSubmitting(false);
    }
  };
  const selectBusinessType = (businessType: string): void => {
    setForm((current) => {
      const planStillAvailable = plans.some((plan) => plan.id === current.planId && (!(plan.businessTypeCodes?.length) || plan.businessTypeCodes.includes(businessType)));
      return { ...current, businessType, planId: planStillAvailable ? current.planId : "" };
    });
  };
  const selectPlan = (planId: string): void => {
    const plan = plans.find((item) => item.id === planId);
    setForm((current) =>
      plan
        ? {
            ...current,
            planId,
            planName: plan.name,
            maxCustomerAccounts: String(plan.maxCustomerAccounts),
            maxStaff: String(plan.maxStaff),
            maxLeads: String(plan.maxLeads),
            maxCatalogItems: String(plan.maxCatalogItems),
            smsEnabled: plan.smsEnabled,
            smsCreditEnabled: plan.smsCreditEnabled,
            smsCreditLimit: String(plan.smsCreditLimit),
            emailEnabled: plan.emailEnabled,
            socialEnabled: plan.socialEnabled,
          }
        : { ...current, planId: "" },
    );
  };
  const updateLicense = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!editing) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const updated = await platformApi.updateLicense(editing.id, {
        planName: editing.subscription.planName,
        startsAtUtc: editing.subscription.startsAtUtc,
        endsAtUtc: editing.subscription.endsAtUtc,
        maxCustomerAccounts: editing.subscription.maxCustomerAccounts,
        maxStaff: editing.subscription.maxStaff,
        maxLeads: editing.subscription.maxLeads,
        maxCatalogItems: editing.subscription.maxCatalogItems,
        isActive: editing.subscription.isActive,
        smsEnabled: editing.subscription.smsEnabled,
        emailEnabled: editing.channels.emailAllowed,
        socialEnabled: editing.channels.socialAllowed,
        planId: editing.subscription.planId,
      });
      setTenants((current) =>
        current.map((tenant) => (tenant.id === updated.id ? updated : tenant)),
      );
      setEditing(null);
      setNotice(`لایسنس «${updated.name}» به‌روزرسانی شد.`);
    } catch (updateError) {
      setError(getErrorMessage(updateError));
    } finally {
      setSubmitting(false);
    }
  };
  const openCompanyEditor = (tenant: PlatformTenant): void =>
    setEditingCompany({
      tenantId: tenant.id,
      name: tenant.name,
      code: tenant.code,
      businessType: tenant.businessType,
      displayName: tenant.administratorDisplayName ?? "",
      username: tenant.administratorUsername ?? "",
      email: tenant.initialOwner?.email ?? "",
      mobile: tenant.administratorMobile ?? "",
      newPassword: "",
    });
  const saveCompany = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!editingCompany) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const company = await platformApi.updateTenant(editingCompany.tenantId, {
        name: editingCompany.name,
        code: editingCompany.code,
        businessType: editingCompany.businessType,
      });
      const updated = await platformApi.updateAdministrator(
        editingCompany.tenantId,
        {
          displayName: editingCompany.displayName,
          username: editingCompany.username,
          email: editingCompany.email,
          mobile: editingCompany.mobile.trim() || null,
          newPassword: editingCompany.newPassword || null,
        },
      );
      setTenants((current) =>
        current.map((tenant) => (tenant.id === company.id ? updated : tenant)),
      );
      setEditingCompany(null);
      setNotice(`اطلاعات شرکت «${updated.name}» ذخیره شد.`);
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSubmitting(false);
    }
  };
  const toggleTenantStatus = async (tenant: PlatformTenant): Promise<void> => {
    const shouldEnable = tenant.status !== "Active";
    const action = shouldEnable ? "فعال" : "غیرفعال";
    if (!window.confirm(`آیا از ${action} کردن شرکت «${tenant.name}» مطمئن هستید؟${shouldEnable ? "" : " پس از غیرفعال‌سازی، کاربران این شرکت دیگر به CRM دسترسی نخواهند داشت."}`)) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const updated = await platformApi.setTenantEnabled(tenant.id, shouldEnable);
      setTenants((current) => current.map((item) => item.id === updated.id ? updated : item));
      setNotice(`شرکت «${updated.name}» ${shouldEnable ? "فعال" : "غیرفعال"} شد.`);
    } catch (updateError) {
      setError(getErrorMessage(updateError));
    } finally {
      setSubmitting(false);
    }
  };
  const softDeleteTenant = async (tenant: PlatformTenant): Promise<void> => {
    if (!window.confirm(`حذف منطقی شرکت «${tenant.name}» انجام شود؟ دسترسی شرکت فوراً قطع می‌شود، اما اطلاعات برای Audit و بازیابی احتمالی در دیتابیس باقی می‌ماند.`)) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      await platformApi.softDeleteTenant(tenant.id);
      setTenants((current) => current.filter((item) => item.id !== tenant.id));
      setNotice(`شرکت «${tenant.name}» به‌صورت منطقی حذف شد و دسترسی آن قطع گردید.`);
    } catch (deleteError) {
      setError(getErrorMessage(deleteError));
    } finally {
      setSubmitting(false);
    }
  };
  const openCredentialResend = (tenant: PlatformTenant): void => {
    setError(null);
    setNotice(null);
    setCredentialEditor({
      tenantId: tenant.id,
      tenantName: tenant.name,
      managerName: tenant.administratorDisplayName ?? "مدیر شرکت",
      username: tenant.administratorUsername ?? "",
      mobile: tenant.administratorMobile ?? "",
      newPassword: generateTemporaryPassword(),
    });
  };
  const resendAdministratorLogin = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!credentialEditor) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const result = await platformApi.resendAdministratorLogin(
        credentialEditor.tenantId,
        credentialEditor.newPassword,
      );
      if (result.sent) {
        setCredentialEditor(null);
        setNotice(`${result.message} شماره مقصد: ${result.mobile}`);
      } else {
        setError(
          `${result.message}${result.error ? ` ${result.error}` : ""} رمز جدید اعمال شده است؛ برای تلاش مجدد همین رمز موقت را تغییر ندهید.`,
        );
      }
    } catch (sendError) {
      setError(getErrorMessage(sendError));
    } finally {
      setSubmitting(false);
    }
  };

  const openCreditEditor = (tenant: PlatformTenant): void =>
    setCreditEditor({
      tenantId: tenant.id,
      tenantName: tenant.name,
      enabled: tenant.subscription.smsCreditEnabled,
      limit: String(tenant.subscription.smsCreditLimit),
      used: tenant.subscription.smsCreditUsed,
      topUp: "",
    });
  const saveSmsCredit = async (): Promise<void> => {
    if (!creditEditor) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const updated = await platformApi.updateSmsCredit(creditEditor.tenantId, {
        smsCreditEnabled: creditEditor.enabled,
        smsCreditLimit: Number(creditEditor.limit),
      });
      setTenants((current) => current.map((item) => item.id === updated.id ? updated : item));
      setCreditEditor((current) => current ? {
        ...current,
        enabled: updated.subscription.smsCreditEnabled,
        limit: String(updated.subscription.smsCreditLimit),
        used: updated.subscription.smsCreditUsed,
      } : current);
      setNotice(`تنظیمات شارژ پیامک «${updated.name}» ذخیره شد.`);
    } catch (updateError) {
      setError(getErrorMessage(updateError));
    } finally {
      setSubmitting(false);
    }
  };
  const topUpSmsCredit = async (): Promise<void> => {
    if (!creditEditor) return;
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const updated = await platformApi.addSmsCredit(creditEditor.tenantId, Number(creditEditor.topUp));
      setTenants((current) => current.map((item) => item.id === updated.id ? updated : item));
      setCreditEditor((current) => current ? {
        ...current,
        limit: String(updated.subscription.smsCreditLimit),
        used: updated.subscription.smsCreditUsed,
        topUp: "",
      } : current);
      setNotice(`${number(Number(creditEditor.topUp))} پیامک به اعتبار «${updated.name}» اضافه شد.`);
    } catch (updateError) {
      setError(getErrorMessage(updateError));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <main className="min-h-[calc(100vh-74px)] text-slate-900" dir="rtl">
      {error || notice ? (
        <div
          aria-live="assertive"
          className={`fixed left-4 right-4 top-[86px] z-[90] flex items-start gap-3 rounded-2xl border px-4 py-4 text-sm font-bold shadow-2xl backdrop-blur sm:left-6 sm:right-auto sm:w-[430px] ${
            error
              ? "border-rose-200 bg-rose-50/95 text-rose-800 shadow-rose-950/10"
              : "border-emerald-200 bg-emerald-50/95 text-emerald-800 shadow-emerald-950/10"
          }`}
          role="alert"
        >
          <span className={`grid size-9 shrink-0 place-items-center rounded-xl ${error ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
            <AppIcon name={error ? "close" : "check"} size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-black">{error ? "خطا / نیاز به اقدام" : "عملیات موفق"}</p>
            <p className="mt-1 break-words text-xs leading-6">{error ?? notice}</p>
          </div>
          <button
            aria-label="بستن اعلان"
            className="grid size-8 shrink-0 place-items-center rounded-lg hover:bg-black/5"
            onClick={() => { setError(null); setNotice(null); }}
            type="button"
          >
            <AppIcon name="close" size={16} />
          </button>
        </div>
      ) : null}
      <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-7">
        <section className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-bold text-brand-600">پنل مرکزی</p>
            <h1 className="mt-1 text-2xl font-black text-ink-950 sm:text-3xl">
              مدیریت شرکت‌ها و لایسنس‌ها
            </h1>
            <p className="mt-2 text-sm leading-7 text-slate-500">
              هر شرکت فضای جداگانه، مدیر اولیه و سقف مصرف مخصوص خود را دارد.
            </p>
          </div>
          <div className="w-full rounded-2xl bg-slate-950 px-4 py-3 text-right text-white sm:w-auto sm:min-w-32">
            <p className="text-xs text-slate-400">تعداد شرکت‌ها</p>
            <p className="mt-1 text-xl font-black">{number(tenants.length)}</p>
          </div>
        </section>
        {createdLogin ? (
          <section className="rounded-3xl border border-brand-100 bg-brand-50 p-5 sm:flex sm:items-center sm:justify-between sm:gap-5">
            <div className="min-w-0">
              <p className="text-sm font-black text-brand-800">
                اطلاعات ورود شرکت آماده است
              </p>
              <p className="mt-2 break-words text-sm leading-7 text-brand-700">
                کد سازمانی:{" "}
                <span className="break-all font-sans font-black" dir="ltr">
                  {createdLogin.code}
                </span>{" "}
                · نام کاربری:{" "}
                <span className="break-all font-sans font-black" dir="ltr">
                  {createdLogin.username}
                </span>
              </p>
              <p className="mt-1 text-xs text-brand-600">
                {createdLogin.reusedExistingUser
                  ? "این مدیر از قبل حساب داشته است؛ رمز عبور قبلی او بدون تغییر برای ورود به شرکت جدید استفاده می‌شود."
                  : "رمز عبور همان رمز اولیه‌ای است که هنگام ساخت شرکت وارد شد."}
              </p>
              <p className={`mt-2 text-xs font-black ${createdLogin.smsSent ? "text-emerald-700" : "text-amber-700"}`}>
                {createdLogin.smsSent
                  ? createdLogin.reusedExistingUser
                    ? "پیامک دسترسی شرکت جدید برای شماره ثبت‌شده حساب مدیر ارسال شد؛ رمز قبلی معتبر است."
                    : "پیامک لینک ورود، نام کاربری و رمز اولیه برای مدیرعامل ارسال شد."
                  : `ارسال پیامک انجام نشد${createdLogin.smsError ? `: ${createdLogin.smsError}` : "."}`}
              </p>
            </div>
            <a
              className="mt-4 inline-flex shrink-0 items-center justify-center rounded-xl bg-brand-600 px-4 py-3 text-sm font-black text-white shadow-lg shadow-brand-500/20 sm:mt-0"
              href={`/login?tenant=${encodeURIComponent(createdLogin.code)}`}
            >
              ورود شرکت <AppIcon name="arrow-left" size={16} />
            </a>
          </section>
        ) : null}
        <section className="grid gap-3 sm:grid-cols-2" aria-label="دسترسی سریع مدیریت پلتفرم">
          <button className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 text-right transition hover:border-emerald-300 hover:bg-emerald-50" onClick={() => focusPlatformSection("company")} type="button">
            <span className="grid size-9 place-items-center rounded-xl bg-white text-emerald-600 shadow-sm"><AppIcon name="plus" size={18} /></span>
            <span className="mt-3 block text-sm font-black text-slate-800">تعریف شرکت</span>
            <span className="mt-1 block text-xs leading-6 text-slate-500">ایجاد سازمان، مدیر اولیه و لایسنس</span>
          </button>
          <button className="rounded-2xl border border-violet-100 bg-violet-50/60 p-4 text-right transition hover:border-violet-300 hover:bg-violet-50" onClick={() => focusPlatformSection("tenants")} type="button">
            <span className="grid size-9 place-items-center rounded-xl bg-white text-violet-600 shadow-sm"><AppIcon name="building" size={18} /></span>
            <span className="mt-3 block text-sm font-black text-slate-800">شرکت‌ها و لایسنس‌ها</span>
            <span className="mt-1 block text-xs leading-6 text-slate-500">ویرایش ظرفیت، اعتبار و دسترسی ارسال</span>
          </button>
        </section>
        <section className="grid min-w-0 gap-6">
          <section id="platform-company-create" className="scroll-mt-24 min-w-0 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <button
              aria-expanded={isCreateFormOpen}
              className="flex w-full items-center gap-3 text-right"
              onClick={() => setIsCreateFormOpen((current) => !current)}
              type="button"
            >
              <span className={`grid size-10 shrink-0 place-items-center rounded-xl transition ${isCreateFormOpen ? "bg-slate-950 text-white" : "bg-brand-50 text-brand-600"}`}>
                <AppIcon name={isCreateFormOpen ? "close" : "plus"} size={19} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-black text-ink-950">ایجاد شرکت جدید</span>
                <span className="mt-1 block text-xs leading-6 text-slate-400">برای تعریف شرکت، مدیر اولیه و لایسنس، این بخش را باز کنید.</span>
              </span>
              <span className="shrink-0 text-xs font-bold text-brand-600">{isCreateFormOpen ? "بستن" : "باز کردن"}</span>
            </button>
            {isCreateFormOpen ? <form
              className="mt-6"
              onSubmit={(event) => void createTenant(event)}
            >
            <fieldset className="rounded-2xl border border-brand-100 bg-brand-50/40 p-4">
              <legend className="px-1 text-sm font-black text-slate-800">نوع کسب‌وکار و قالب پیشنهادی</legend>
              <p className="mt-1 text-xs leading-6 text-slate-500">با انتخاب نوع کسب‌وکار، قالب قابلیت همان نوع در لحظه ساخت شرکت اعمال می‌شود. سقف‌ها و کانال‌های ارسال همچنان از پلن یا لایسنس اختصاصی خوانده می‌شوند.</p>
              <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                {activeBusinessTypes.map((item) => {
                  const selected = form.businessType === item.code;
                  return (
                    <label className={`cursor-pointer rounded-2xl border p-3 transition ${selected ? "border-brand-400 bg-white shadow-sm" : "border-transparent bg-white/70 hover:border-brand-200"}`} key={item.id}>
                      <input checked={selected} className="sr-only" name="businessType" onChange={() => selectBusinessType(item.code)} type="radio" />
                      <span className="block text-sm font-black text-slate-800">{item.name}</span>
                      <span className="mt-1 block text-[11px] font-bold text-brand-600">قالب {item.name}</span>
                      <span className="mt-1.5 block text-[10px] leading-5 text-slate-400">{item.description || `نمایش با عنوان ${item.entityName} در پنل شرکت`}</span>
                    </label>
                  );
                })}
              </div>
              {!activeBusinessTypes.length ? <p className="mt-4 rounded-xl bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700">نوع فعالی برای انتخاب وجود ندارد. ابتدا از بخش «اطلاعات پایه» یک نوع کسب‌وکار فعال تعریف کنید.</p> : null}
              <div className="mt-4 rounded-xl border border-white bg-white/80 px-3 py-2.5 text-xs leading-6 text-slate-600">
                <p>قالبی که برای این شرکت اعمال می‌شود: <span className="font-black text-brand-700">{selectedBusinessType.template}</span></p>
                <div className="mt-2 flex flex-wrap gap-1.5">{selectedBusinessType.featureCodes.length ? selectedBusinessType.featureCodes.map((code) => <span className="rounded-full bg-brand-50 px-2 py-1 font-mono text-[10px] font-bold text-brand-700" dir="ltr" key={code}>{code}</span>) : <span className="font-bold text-amber-700">این قالب هنوز قابلیتی ندارد.</span>}</div>
              </div>
            </fieldset>
            <label className="mt-6 block">
              <span className="mb-1.5 block text-xs font-bold text-slate-500">
                انتخاب پلن
              </span>
              <select
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500"
                onChange={(event) => selectPlan(event.target.value)}
                value={form.planId}
              >
                <option value="">پلن اختصاصی (مقادیر دستی)</option>
                {availablePlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} — {limit(plan.maxCustomerAccounts)} مشتری،{" "}
                    {limit(plan.maxStaff)} پرسنل، {limit(plan.maxLeads)} سرنخ، {limit(plan.maxCatalogItems)} کاتالوگ
                  </option>
                ))}
              </select>
            </label>
            {form.businessType && !availablePlans.length ? <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-bold leading-6 text-amber-700">برای این نوع کسب‌وکار، پلن متصلی تعریف نشده است. می‌توانید لایسنس اختصاصی ثبت کنید یا اتصال پلن را در «اطلاعات پایه» انجام دهید.</p> : null}
            {form.planId ? (
              <p className="mt-3 rounded-xl bg-brand-50 px-3 py-2 text-xs font-bold leading-6 text-brand-700">
                ظرفیت‌ها و مجوزهای پلن انتخاب‌شده در زمان ایجاد شرکت اعمال می‌شوند.
              </p>
            ) : null}
            <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {(
                [
                  ["name", "نام شرکت", "text"],
                  ["code", "کد سازمانی (انگلیسی)", "text"],
                  ["planName", "نام پلن", "text"],
                  ["ownerDisplayName", "نام مدیر شرکت", "text"],
                  ["ownerUsername", "نام کاربری ورود", "text"],
                  ["ownerEmail", "ایمیل مدیر", "email"],
                  ["ownerMobile", "موبایل مدیرعامل", "tel"],
                  ["maxCustomerAccounts", "حداکثر مشتری", "number"],
                  ["maxStaff", "حداکثر پرسنل", "number"],
                  ["maxLeads", "حداکثر سرنخ", "number"],
                  ["maxCatalogItems", "حداکثر کاتالوگ یا خدمت", "number"],
                ] as Array<[Exclude<keyof TenantForm, "smsEnabled" | "smsCreditEnabled" | "emailEnabled" | "socialEnabled">, string, string]>
              ).map(([field, label, type]) => (
                <label key={field}>
                  <span className="mb-1.5 block text-xs font-bold text-slate-500">
                    {label}
                  </span>
                  <input
                    autoComplete={field === "ownerEmail" ? "email" : field === "ownerUsername" ? "username" : field === "ownerMobile" ? "tel" : undefined}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-50"
                    dir={["code", "ownerUsername", "ownerEmail", "ownerMobile"].includes(field) ? "ltr" : undefined}
                    inputMode={field === "ownerMobile" ? "tel" : type === "number" ? "numeric" : undefined}
                    maxLength={field === "name" || field === "ownerDisplayName" ? 250 : field === "code" || field === "ownerUsername" ? 80 : undefined}
                    min={type === "number" ? 0 : undefined}
                    minLength={field === "ownerUsername" ? 3 : undefined}
                    onChange={(event) => update(field, event.target.value)}
                    pattern={field === "code" ? "[A-Za-z0-9-]+" : field === "ownerUsername" ? "[A-Za-z0-9._@-]{3,80}" : undefined}
                    required
                    title={field === "code" ? "فقط حروف انگلیسی، عدد و خط تیره" : field === "ownerUsername" ? "۳ تا ۸۰ کاراکتر؛ حروف انگلیسی، عدد، نقطه، زیرخط، @ یا خط تیره" : undefined}
                    type={type}
                    value={form[field]}
                  />
                  {field === "ownerUsername" ? (
                    <span className="mt-1.5 block text-[11px] leading-5 text-slate-400">
                      حداقل ۳ کاراکتر؛ فقط حروف انگلیسی، عدد، نقطه، زیرخط، @ یا خط تیره
                    </span>
                  ) : field === "code" ? (
                    <span className="mt-1.5 block text-[11px] leading-5 text-slate-400">
                      کد ورود سازمان؛ فقط حروف انگلیسی، عدد و خط تیره
                    </span>
                  ) : null}
                </label>
              ))}
              <label className="sm:col-span-2 xl:col-span-3">
                <span className="mb-1.5 block text-xs font-bold text-slate-500">
                  رمز اولیه مدیر شرکت
                </span>
                <input
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-50"
                  minLength={8}
                  onChange={(event) =>
                    update("ownerPassword", event.target.value)
                  }
                  required
                  type="password"
                  value={form.ownerPassword}
                />
              </label>
              <label className="flex items-center justify-between gap-4 rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4 sm:col-span-2 xl:col-span-3">
                <span className="min-w-0">
                  <span className="block text-sm font-black text-slate-700">مجوز ارسال پیامک سرنخ</span>
                  <span className="mt-1 block text-xs leading-6 text-slate-500">اگر فعال باشد، شرکت می‌تواند پس از ثبت سرنخ از سرویس‌دهنده پیامک فعال پلتفرم پیام معرفی ارسال کند.</span>
                </span>
                <input checked={form.smsEnabled} className="size-5 shrink-0 accent-emerald-600" onChange={(event) => setForm((current) => ({ ...current, smsEnabled: event.target.checked }))} type="checkbox" />
              </label>
              <div className="grid gap-3 sm:col-span-2 sm:grid-cols-2 xl:col-span-3">
                <label className="flex items-center justify-between gap-4 rounded-2xl border border-sky-100 bg-sky-50/50 p-4">
                  <span><span className="block text-sm font-black text-slate-700">مجوز ارسال ایمیل</span><span className="mt-1 block text-xs leading-6 text-slate-500">ارسال معرفی به ایمیل سرنخ با SMTP سازمان.</span></span>
                  <input checked={form.emailEnabled} className="size-5 shrink-0 accent-sky-600" onChange={(event) => setForm((current) => ({ ...current, emailEnabled: event.target.checked }))} type="checkbox" />
                </label>
                <label className="flex items-center justify-between gap-4 rounded-2xl border border-fuchsia-100 bg-fuchsia-50/50 p-4">
                  <span><span className="block text-sm font-black text-slate-700">مجوز شبکه اجتماعی</span><span className="mt-1 block text-xs leading-6 text-slate-500">ارسال از Webhook متصل‌شده به سازمان.</span></span>
                  <input checked={form.socialEnabled} className="size-5 shrink-0 accent-fuchsia-600" onChange={(event) => setForm((current) => ({ ...current, socialEnabled: event.target.checked }))} type="checkbox" />
                </label>
              </div>
              <div className="rounded-2xl border border-violet-100 bg-violet-50/50 p-4 sm:col-span-2 xl:col-span-3">
                <label className="flex items-center justify-between gap-4">
                  <span className="min-w-0">
                    <span className="block text-sm font-black text-slate-700">فعال‌سازی شارژ پیامک</span>
                    <span className="mt-1 block text-xs leading-6 text-slate-500">ارسال پیامک فقط وقتی انجام می‌شود که هم مجوز پیامک و هم شارژ پیامک فعال باشند.</span>
                  </span>
                  <input checked={form.smsCreditEnabled} className="size-5 shrink-0 accent-violet-600" onChange={(event) => setForm((current) => ({ ...current, smsCreditEnabled: event.target.checked }))} type="checkbox" />
                </label>
                <label className="mt-4 block">
                  <span className="mb-1.5 block text-xs font-bold text-slate-500">اعتبار اولیه پیامک</span>
                  <input className="w-full rounded-xl border border-violet-100 bg-white px-3 py-2.5 text-sm outline-none focus:border-violet-500 disabled:opacity-60" disabled={!form.smsCreditEnabled} min={0} onChange={(event) => update("smsCreditLimit", event.target.value)} required type="number" value={form.smsCreditLimit} />
                  <span className="mt-1.5 block text-[11px] font-bold text-violet-700">عدد صفر یعنی اعتبار پیامک نامحدود است.</span>
                </label>
              </div>
              <label className="xl:col-span-1">
                <span className="mb-1.5 block text-xs font-bold text-slate-500">
                  شروع لایسنس
                </span>
                <JalaliDatePicker
                  onChange={(value) => update("startsAt", value ? toJalaliInput(value.toDate()) : "")}
                  placeholder="شروع را به شمسی انتخاب کنید"
                  value={toPickerValue(form.startsAt)}
                />
              </label>
              <label className="xl:col-span-1">
                <span className="mb-1.5 block text-xs font-bold text-slate-500">
                  پایان لایسنس
                </span>
                <JalaliDatePicker
                  onChange={(value) => update("endsAt", value ? toJalaliInput(value.toDate()) : "")}
                  placeholder="پایان را به شمسی انتخاب کنید"
                  value={toPickerValue(form.endsAt)}
                />
              </label>
            </div>
            <button
              className="mt-6 w-full rounded-xl bg-brand-600 py-3 text-sm font-black text-white shadow-lg shadow-brand-500/20 hover:bg-brand-700 disabled:opacity-60"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "در حال ایجاد…" : "ایجاد شرکت و لایسنس"}
            </button>
            </form> : null}
          </section>
          <section id="platform-tenants" className="scroll-mt-24 min-w-0 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-black text-ink-950">شرکت‌ها</h2>
                <p className="mt-1 text-xs text-slate-400">
                  وضعیت و ظرفیت هر فضای CRM
                </p>
              </div>
              <label className="flex w-full min-w-0 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 sm:w-auto">
                <AppIcon name="search" size={16} className="text-slate-400" />
                <input
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400 sm:w-44 sm:flex-none"
                  onChange={(event) => setFilter(event.target.value)}
                  placeholder="جست‌وجوی شرکت"
                  value={filter}
                />
              </label>
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-2 2xl:hidden">
              {loading ? (
                <div className="rounded-2xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400 md:col-span-2">
                  در حال دریافت اطلاعات…
                </div>
              ) : visibleTenants.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400 md:col-span-2">
                  شرکتی یافت نشد.
                </div>
              ) : (
                visibleTenants.map((tenant) => (
                  <TenantCard
                    businessTypes={businessTypes}
                    key={tenant.id}
                    onEditCompany={openCompanyEditor}
                    onEditCredit={openCreditEditor}
                    onEditLicense={(item) => setEditing(structuredClone(item))}
                    onResendLogin={openCredentialResend}
                    onToggleTenant={(item) => void toggleTenantStatus(item)}
                    onDeleteTenant={(item) => void softDeleteTenant(item)}
                    tenant={tenant}
                  />
                ))
              )}
            </div>
            <div className="mt-5 hidden overflow-x-auto 2xl:block">
              <table className="w-full min-w-[1220px] text-right">
                <thead className="border-y border-slate-100 text-xs text-slate-400">
                  <tr>
                    <th className="py-3 font-bold">شرکت</th>
                    <th className="py-3 font-bold">لایسنس</th>
                    <th className="py-3 font-bold">اعتبار</th>
                    <th className="py-3 font-bold">مصرف اشتراک</th>
                    <th className="py-3 font-bold">سقف پلن</th>
                    <th className="py-3 font-bold">وضعیت</th>
                    <th className="py-3 font-bold">کانال‌های ارسال</th>
                    <th className="py-3 font-bold">شارژ پیامک</th>
                    <th className="py-3" />
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        className="py-10 text-center text-sm text-slate-400"
                        colSpan={9}
                      >
                        در حال دریافت اطلاعات…
                      </td>
                    </tr>
                  ) : visibleTenants.length === 0 ? (
                    <tr>
                      <td
                        className="py-10 text-center text-sm text-slate-400"
                        colSpan={9}
                      >
                        شرکتی یافت نشد.
                      </td>
                    </tr>
                  ) : (
                    visibleTenants.map((tenant) => (
                      <tr
                        className="border-b border-slate-50 last:border-0"
                        key={tenant.id}
                      >
                        <td className="py-4">
                          <p className="text-sm font-black text-slate-700">
                            {tenant.name}
                          </p>
                          <p className="mt-1 font-sans text-xs text-slate-400">
                            {tenant.code}
                          </p>
                          <p className="mt-1 text-[10px] font-bold text-brand-600">
                            {businessTypeInfo(tenant.businessType, businessTypes).label} · {tenant.suggestedTemplate?.name ?? businessTypeInfo(tenant.businessType, businessTypes).template}
                          </p>
                        </td>
                        <td className="py-4 text-sm font-bold text-slate-600">
                          {tenant.subscription.planName}
                        </td>
                        <td className="py-4 text-xs text-slate-500">
                          {date(tenant.subscription.endsAtUtc)}
                        </td>
                        <td className="py-4 pl-6">
                          <div className="space-y-3">
                            <UsageMeter
                              label="مشتری"
                              maximum={tenant.subscription.maxCustomerAccounts}
                              used={tenant.usage.customerAccounts}
                            />
                            <UsageMeter
                              label="پرسنل فعال"
                              maximum={tenant.subscription.maxStaff}
                              used={tenant.usage.activeStaff}
                            />
                          </div>
                        </td>
                        <td className="py-4 text-xs leading-6 text-slate-500">
                          {limit(tenant.subscription.maxCustomerAccounts)}{" "}
                          مشتری
                          <br />
                          {limit(tenant.subscription.maxStaff)} پرسنل
                        </td>
                        <td className="py-4">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-bold ${tenant.status === "Active" && tenant.subscription.isUsable ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}
                          >
                            {tenant.status !== "Active"
                              ? "شرکت غیرفعال"
                              : tenant.subscription.isUsable
                                ? "فعال"
                                : "لایسنس غیرفعال / منقضی"}
                          </span>
                        </td>
                        <td className="py-4">
                          <div className="flex flex-wrap gap-1 text-[10px] font-black">
                            <span className={`rounded-full px-2 py-1 ${tenant.channels.emailAllowed ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-400"}`}>ایمیل</span>
                            <span className={`rounded-full px-2 py-1 ${tenant.channels.smsAllowed ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>پیامک</span>
                            <span className={`rounded-full px-2 py-1 ${tenant.channels.socialAllowed ? "bg-fuchsia-50 text-fuchsia-700" : "bg-slate-100 text-slate-400"}`}>شبکه اجتماعی</span>
                          </div>
                        </td>
                        <td className="py-4">
                          <p className={`text-xs font-black ${tenant.subscription.smsCreditEnabled ? "text-violet-700" : "text-slate-400"}`}>
                            {!tenant.subscription.smsCreditEnabled
                              ? "غیرفعال"
                              : tenant.subscription.smsCreditUnlimited
                                ? "نامحدود"
                                : `${number(tenant.subscription.smsCreditRemaining ?? 0)} باقی‌مانده`}
                          </p>
                          {tenant.subscription.smsCreditEnabled && !tenant.subscription.smsCreditUnlimited ? (
                            <p className="mt-1 text-[10px] text-slate-400">{number(tenant.subscription.smsCreditUsed)} مصرف از {number(tenant.subscription.smsCreditLimit)}</p>
                          ) : null}
                        </td>
                        <td className="py-4">
                          <button
                            className="ml-2 rounded-lg bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100"
                            onClick={() => openCompanyEditor(tenant)}
                            type="button"
                          >
                            ویرایش شرکت
                          </button>
                          <button
                            className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-bold text-brand-600 hover:bg-brand-50"
                            onClick={() => setEditing(structuredClone(tenant))}
                            type="button"
                          >
                            ویرایش لایسنس
                          </button>
                          <button
                            className="mt-2 rounded-lg bg-violet-50 px-3 py-2 text-xs font-bold text-violet-700 hover:bg-violet-100"
                            onClick={() => openCreditEditor(tenant)}
                            type="button"
                          >
                            مدیریت شارژ
                          </button>
                          <button
                            className="mt-2 mr-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-100"
                            onClick={() => openCredentialResend(tenant)}
                            type="button"
                          >
                            ارسال مجدد اطلاعات ورود
                          </button>
                          <button
                            className={`mt-2 rounded-lg px-3 py-2 text-xs font-bold ${tenant.status === "Active" ? "bg-amber-50 text-amber-700 hover:bg-amber-100" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                            onClick={() => void toggleTenantStatus(tenant)}
                            type="button"
                          >
                            {tenant.status === "Active" ? "غیرفعال کردن" : "فعال کردن"}
                          </button>
                          <button
                            className="mt-2 mr-2 rounded-lg bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 hover:bg-rose-100"
                            onClick={() => void softDeleteTenant(tenant)}
                            type="button"
                          >
                            حذف منطقی
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </section>
      </div>
      {credentialEditor ? (
        <div className="fixed inset-0 z-[70] grid place-items-end bg-slate-950/50 sm:place-items-center sm:p-4">
          <button
            aria-label="بستن ارسال مجدد اطلاعات ورود"
            className="absolute inset-0"
            onClick={() => setCredentialEditor(null)}
            type="button"
          />
          <form
            className="relative z-10 w-full rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-xl sm:rounded-3xl sm:p-7"
            onSubmit={(event) => void resendAdministratorLogin(event)}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-bold text-emerald-600">ارسال مجدد اطلاعات ورود مدیرعامل</p>
                <h2 className="mt-1 truncate text-xl font-black text-ink-950">{credentialEditor.tenantName}</h2>
                <p className="mt-2 text-xs leading-6 text-slate-500">
                  برای امنیت، رمز فعلی قابل بازیابی نیست. با تأیید این عملیات، رمز مدیرعامل به رمز موقت زیر تغییر می‌کند، نشست‌های قبلی او بسته می‌شود و لینک ورود، نام کاربری و رمز جدید برای موبایل ثبت‌شده پیامک خواهد شد.
                </p>
              </div>
              <button
                className="grid size-9 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-500"
                onClick={() => setCredentialEditor(null)}
                type="button"
              >
                <AppIcon name="close" size={18} />
              </button>
            </div>

            <div className="mt-5 grid gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4 sm:grid-cols-2">
              <div>
                <p className="text-[11px] font-bold text-slate-400">مدیرعامل / مدیر شرکت</p>
                <p className="mt-1 text-sm font-black text-slate-700">{credentialEditor.managerName}</p>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-400">شماره موبایل مقصد</p>
                <p className="mt-1 font-sans text-sm font-black text-slate-700" dir="ltr">{credentialEditor.mobile || "ثبت نشده"}</p>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-400">نام کاربری</p>
                <p className="mt-1 break-all font-sans text-sm font-black text-slate-700" dir="ltr">{credentialEditor.username || "—"}</p>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-400">وضعیت ارسال</p>
                <p className={`mt-1 text-xs font-black ${credentialEditor.mobile ? "text-emerald-700" : "text-rose-700"}`}>
                  {credentialEditor.mobile ? "آماده ارسال از سرویس پیامک فعال" : "ابتدا موبایل مدیر را ثبت کنید"}
                </p>
              </div>
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-xs font-bold text-slate-500">رمز موقت جدید</span>
              <div className="flex gap-2">
                <input
                  autoComplete="new-password"
                  className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3.5 py-3 font-sans text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50"
                  dir="ltr"
                  minLength={8}
                  onChange={(event) =>
                    setCredentialEditor((current) =>
                      current ? { ...current, newPassword: event.target.value } : current,
                    )
                  }
                  required
                  type="text"
                  value={credentialEditor.newPassword}
                />
                <button
                  className="shrink-0 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-black text-slate-600 hover:bg-slate-100"
                  onClick={() =>
                    setCredentialEditor((current) =>
                      current ? { ...current, newPassword: generateTemporaryPassword() } : current,
                    )
                  }
                  type="button"
                >
                  ساخت رمز جدید
                </button>
              </div>
              <span className="mt-2 block text-[11px] leading-5 text-amber-700">
                اگر ارسال پیامک ناموفق شود، همین رمز جدید در سیستم اعمال شده است. در تلاش مجدد آن را تغییر ندهید مگر اینکه قصد Reset دوباره داشته باشید.
              </span>
            </label>

            <button
              className="mt-6 w-full rounded-xl bg-emerald-600 py-3 text-sm font-black text-white shadow-lg shadow-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={submitting || !credentialEditor.mobile || credentialEditor.newPassword.length < 8}
              type="submit"
            >
              {submitting ? "در حال بازنشانی و ارسال…" : "بازنشانی رمز و ارسال اطلاعات ورود"}
            </button>
          </form>
        </div>
      ) : null}
      {editingCompany ? (
        <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/45 sm:place-items-center sm:p-4">
          <button
            aria-label="بستن ویرایش شرکت"
            className="absolute inset-0"
            onClick={() => setEditingCompany(null)}
            type="button"
          />
          <form
            className="relative z-10 max-h-[calc(100dvh-1rem)] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:max-h-[90vh] sm:max-w-2xl sm:rounded-3xl sm:p-6"
            onSubmit={(event) => void saveCompany(event)}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-brand-600">
                  ویرایش شرکت و مدیر
                </p>
                <h2 className="mt-1 text-xl font-black text-ink-950">
                  {editingCompany.name}
                </h2>
                <p className="mt-1 text-xs text-slate-400">
                  رمز جدید را خالی بگذارید تا رمز فعلی تغییر نکند.
                </p>
              </div>
              <button
                className="rounded-xl p-2 text-slate-400 hover:bg-slate-50"
                onClick={() => setEditingCompany(null)}
                type="button"
              >
                <AppIcon name="close" />
              </button>
            </div>
            <label className="mt-5 block">
              <span className="mb-1.5 block text-xs font-bold text-slate-500">نوع کسب‌وکار</span>
              <select
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-50"
                onChange={(event) => setEditingCompany((current) => current ? { ...current, businessType: event.target.value } : current)}
                value={editingCompany.businessType}
              >
                {businessTypes.filter((item) => item.isActive || item.code === editingCompany.businessType).map((item) => <option key={item.id} value={item.code}>{item.name} — قالب {item.name}</option>)}
              </select>
              <span className="mt-1.5 block text-[11px] leading-5 text-slate-400">قالب «{businessTypeInfo(editingCompany.businessType, businessTypes).template}» هنگام ساخت شرکت اعمال شده است. تغییر نوع برای شرکت موجود، قابلیت‌ها را خودکار تغییر نمی‌دهد تا تنظیمات اختصاصی شرکت محفوظ بماند.</span>
            </label>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {(
                [
                  ["name", "نام شرکت", "text"],
                  ["code", "کد سازمانی", "text"],
                  ["displayName", "نام مدیر شرکت", "text"],
                  ["username", "نام کاربری ورود", "text"],
                  ["email", "ایمیل مدیر", "email"],
                  ["mobile", "موبایل مدیر", "tel"],
                  ["newPassword", "رمز جدید مدیر", "password"],
                ] as Array<[keyof CompanyEditor, string, string]>
              ).map(([field, label, type]) => (
                <label key={field}>
                  <span className="mb-1.5 block text-xs font-bold text-slate-500">
                    {label}
                  </span>
                  <input
                    autoComplete={
                      field === "newPassword" ? "new-password" : undefined
                    }
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-50"
                    minLength={field === "newPassword" ? 8 : undefined}
                    onChange={(event) =>
                      setEditingCompany((current) =>
                        current
                          ? { ...current, [field]: event.target.value }
                          : current,
                      )
                    }
                    required={field !== "mobile" && field !== "newPassword"}
                    type={type}
                    value={editingCompany[field]}
                  />
                </label>
              ))}
            </div>
            <button
              className="mt-6 w-full rounded-xl bg-slate-950 py-3 text-sm font-black text-white disabled:opacity-60"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "در حال ذخیره…" : "ذخیره اطلاعات شرکت و مدیر"}
            </button>
          </form>
        </div>
      ) : null}
      {creditEditor ? (
        <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/45 sm:place-items-center sm:p-4">
          <button aria-label="بستن مدیریت شارژ" className="absolute inset-0" onClick={() => setCreditEditor(null)} type="button" />
          <section className="relative z-10 max-h-[92dvh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-xl sm:rounded-3xl sm:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-violet-600">مدیریت شارژ پیامک</p>
                <h2 className="mt-1 text-xl font-black text-ink-950">{creditEditor.tenantName}</h2>
                <p className="mt-2 text-xs leading-6 text-slate-500">هر ارسال موفق یک واحد از اعتبار کم می‌کند؛ ارسال ناموفق به‌صورت خودکار برگشت می‌خورد.</p>
              </div>
              <button className="grid size-9 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-500" onClick={() => setCreditEditor(null)} type="button"><AppIcon name="close" size={18} /></button>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-slate-50 p-4"><p className="text-xs text-slate-400">مصرف‌شده</p><p className="mt-2 text-xl font-black text-slate-800">{number(creditEditor.used)}</p></div>
              <div className="rounded-2xl bg-violet-50 p-4"><p className="text-xs text-violet-500">باقی‌مانده فعلی</p><p className="mt-2 text-xl font-black text-violet-800">{Number(creditEditor.limit) === 0 ? "نامحدود" : number(Math.max(0, Number(creditEditor.limit) - creditEditor.used))}</p></div>
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 p-4">
              <label className="flex items-center justify-between gap-4">
                <span><span className="block text-sm font-black text-slate-700">فعال بودن شارژ پیامک</span><span className="mt-1 block text-xs text-slate-400">بدون فعال‌بودن این گزینه، حتی با وجود مجوز پیامک ارسالی انجام نمی‌شود.</span></span>
                <input checked={creditEditor.enabled} className="size-5 shrink-0 accent-violet-600" onChange={(event) => setCreditEditor((current) => current ? { ...current, enabled: event.target.checked } : current)} type="checkbox" />
              </label>
              <label className="mt-5 block">
                <span className="mb-2 block text-xs font-bold text-slate-500">سقف کل اعتبار</span>
                <input className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-sm outline-none focus:border-violet-500" min={0} onChange={(event) => setCreditEditor((current) => current ? { ...current, limit: event.target.value } : current)} type="number" value={creditEditor.limit} />
                <span className="mt-2 block text-[11px] font-bold text-violet-700">صفر یعنی نامحدود. این عدد سقف کل است و مصرف قبلی از آن کم می‌شود.</span>
              </label>
              <button className="mt-4 w-full rounded-xl bg-violet-600 py-3 text-sm font-black text-white disabled:opacity-60" disabled={submitting || Number(creditEditor.limit) < 0} onClick={() => void saveSmsCredit()} type="button">{submitting ? "در حال ذخیره…" : "ذخیره تنظیمات شارژ"}</button>
            </div>

            <div className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4">
              <p className="text-sm font-black text-emerald-800">افزودن شارژ</p>
              <p className="mt-1 text-xs leading-6 text-emerald-700">مقدار واردشده به سقف فعلی اضافه می‌شود و سابقه مصرف حفظ خواهد شد.</p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <input className="min-w-0 flex-1 rounded-xl border border-emerald-100 bg-white px-3.5 py-3 text-sm outline-none focus:border-emerald-500" min={1} onChange={(event) => setCreditEditor((current) => current ? { ...current, topUp: event.target.value } : current)} placeholder="تعداد پیامک" type="number" value={creditEditor.topUp} />
                <button className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-black text-white disabled:opacity-50" disabled={submitting || Number(creditEditor.limit) === 0 || Number(creditEditor.topUp) <= 0} onClick={() => void topUpSmsCredit()} type="button">شارژ شرکت</button>
              </div>
              {Number(creditEditor.limit) === 0 ? <p className="mt-3 text-xs font-bold text-amber-700">اعتبار اکنون نامحدود است؛ برای شارژ تعدادی، ابتدا یک سقف غیرصفر ذخیره کنید.</p> : null}
            </div>
            {error ? <p className="mt-4 rounded-xl bg-rose-50 px-3 py-2 text-xs font-bold leading-6 text-rose-700">{error}</p> : null}
          </section>
        </div>
      ) : null}
      {editing ? (
        <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/45 sm:place-items-center sm:p-4">
          <button
            aria-label="بستن ویرایش لایسنس"
            className="absolute inset-0"
            onClick={() => setEditing(null)}
            type="button"
          />
          <form
            className="relative z-10 max-h-[calc(100dvh-1rem)] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:max-h-[90vh] sm:max-w-lg sm:rounded-3xl sm:p-6"
            onSubmit={(event) => void updateLicense(event)}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-brand-600">
                  ویرایش لایسنس
                </p>
                <h2 className="mt-1 text-xl font-black text-ink-950">
                  {editing.name}
                </h2>
              </div>
              <button
                className="rounded-xl p-2 text-slate-400 hover:bg-slate-50"
                onClick={() => setEditing(null)}
                type="button"
              >
                <AppIcon name="close" />
              </button>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="sm:col-span-2">
                <span className="mb-1.5 block text-xs font-bold text-slate-500">
                  نام پلن
                </span>
                <input
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500"
                  onChange={(event) =>
                    setEditing((current) =>
                      current
                        ? {
                            ...current,
                            subscription: {
                              ...current.subscription,
                              planName: event.target.value,
                            },
                          }
                        : current,
                    )
                  }
                  required
                  value={editing.subscription.planName}
                />
              </label>
              <label className="sm:col-span-2">
                <span className="mb-1.5 block text-xs font-bold text-slate-500">پلن مرجع قابلیت‌ها</span>
                <select className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500" onChange={(event) => setEditing((current) => current ? { ...current, subscription: { ...current.subscription, planId: event.target.value || null, planName: event.target.value ? (plans.find((plan) => plan.id === event.target.value)?.name ?? current.subscription.planName) : current.subscription.planName } } : current)} value={editing.subscription.planId ?? ''}><option value="">لایسنس سفارشی / بدون پلن</option>{plans.filter((plan) => plan.isActive).map((plan) => <option key={plan.id} value={plan.id}>{plan.name} · {plan.featureCodes?.length ?? 0} قابلیت</option>)}</select>
                <span className="mt-1.5 block text-[11px] leading-5 text-slate-400">با انتخاب پلن، سقف‌ها و قابلیت‌های پایه از همان پلن خوانده می‌شود. تغییر اختصاصی قابلیت‌ها از صفحه مدیریت قابلیت‌ها انجام می‌شود.</span>
              </label>
              {(
                [
                  ["maxCustomerAccounts", "حداکثر مشتری"],
                  ["maxStaff", "حداکثر پرسنل"],
                  ["maxLeads", "حداکثر سرنخ"],
                  ["maxCatalogItems", "حداکثر کاتالوگ یا خدمت"],
                ] as const
              ).map(([field, label]) => (
                <label key={field}>
                  <span className="mb-1.5 block text-xs font-bold text-slate-500">
                    {label}
                  </span>
                  <input
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500"
                    min={0}
                    onChange={(event) =>
                      setEditing((current) =>
                        current
                          ? {
                              ...current,
                              subscription: {
                                ...current.subscription,
                                [field]: Number(event.target.value),
                              },
                            }
                          : current,
                      )
                    }
                    required
                    type="number"
                    value={editing.subscription[field]}
                  />
                </label>
              ))}
              <p className="sm:col-span-2 rounded-xl bg-brand-50 px-3 py-2 text-xs font-bold leading-6 text-brand-700">برای تمام سقف‌های عددی لایسنس، مقدار صفر به معنی نامحدود و بدون سقف است.</p>
              <label>
                <span className="mb-1.5 block text-xs font-bold text-slate-500">
                  شروع اعتبار (شمسی)
                </span>
                <JalaliDatePicker
                  onChange={(value) => {
                    if (!value) return;
                    const startsAtUtc = asIso(toJalaliInput(value.toDate()));
                    setEditing((current) => current ? {
                      ...current,
                      subscription: { ...current.subscription, startsAtUtc },
                    } : current);
                  }}
                  placeholder="شروع اعتبار را به شمسی انتخاب کنید"
                  value={dayjs(editing.subscription.startsAtUtc)}
                />
              </label>
              <label>
                <span className="mb-1.5 block text-xs font-bold text-slate-500">
                  پایان اعتبار (شمسی)
                </span>
                <JalaliDatePicker
                  onChange={(value) => {
                    if (!value) return;
                    const endsAtUtc = asIso(toJalaliInput(value.toDate()));
                    setEditing((current) => current ? {
                      ...current,
                      subscription: { ...current.subscription, endsAtUtc },
                    } : current);
                  }}
                  placeholder="پایان اعتبار را به شمسی انتخاب کنید"
                  value={dayjs(editing.subscription.endsAtUtc)}
                />
              </label>
              <label className="sm:col-span-2 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-3 text-sm font-bold text-slate-600">
                <input
                  checked={editing.subscription.isActive}
                  onChange={(event) =>
                    setEditing((current) =>
                      current
                        ? {
                            ...current,
                            subscription: {
                              ...current.subscription,
                              isActive: event.target.checked,
                            },
                          }
                        : current,
                    )
                  }
                  type="checkbox"
                />
                فعال بودن لایسنس
              </label>
              <div className="sm:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-black text-slate-800">مجوز کانال‌های ارسال</p>
                <p className="mt-1 text-xs leading-6 text-slate-500">این مجوزها بخشی از لایسنس شرکت هستند. تنظیمات فنی هر کانال در پنل خود شرکت انجام می‌شود.</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <label className="flex items-center justify-between gap-2 rounded-xl bg-white px-3 py-3 text-xs font-black text-sky-700"><span>ایمیل</span><input checked={editing.channels.emailAllowed} className="size-4 accent-sky-600" onChange={(event) => setEditing((current) => current ? { ...current, channels: { ...current.channels, emailAllowed: event.target.checked } } : current)} type="checkbox" /></label>
                  <label className="flex items-center justify-between gap-2 rounded-xl bg-white px-3 py-3 text-xs font-black text-emerald-700"><span>پیامک</span><input checked={editing.channels.smsAllowed} className="size-4 accent-emerald-600" onChange={(event) => setEditing((current) => current ? { ...current, subscription: { ...current.subscription, smsEnabled: event.target.checked }, channels: { ...current.channels, smsAllowed: event.target.checked } } : current)} type="checkbox" /></label>
                  <label className="flex items-center justify-between gap-2 rounded-xl bg-white px-3 py-3 text-xs font-black text-fuchsia-700"><span>شبکه اجتماعی</span><input checked={editing.channels.socialAllowed} className="size-4 accent-fuchsia-600" onChange={(event) => setEditing((current) => current ? { ...current, channels: { ...current.channels, socialAllowed: event.target.checked } } : current)} type="checkbox" /></label>
                </div>
              </div>
            </div>
            <button
              className="mt-6 w-full rounded-xl bg-brand-600 py-3 text-sm font-black text-white disabled:opacity-60"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "در حال ذخیره…" : "ذخیره لایسنس"}
            </button>
          </form>
        </div>
      ) : null}
    </main>
  );
};
